import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  convertRouteLegFormValuesToRouteLeg,
  getLogisticsDataById,
  getPreviousLeg,
  getSegment,
  getUpdatedRouteLegValues,
  saveUpdatedLeg,
} from '@lp/features/RoutePlanner/utils';
import { HandleAircraftReservationType, HandleOpenNewLegModal, RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useResourcesHandlers } from '@lp/features/RoutePlanner/hooks/useResourcesHandlers';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import {
  BaseCommercialAviaLegSchemaValues,
  BaseTmdxDriveAffiliateGroundSchemaValues,
  BaseTmdxDriveTedsGroundSchemaValues,
  LegFormValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useSegmentLocations } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { ACTIVE_GROUND_TRIP_STATUSES } from '@lp/features/RoutePlanner/config';
import {
  useAddAccommodationLeg,
  useAddCommercialLeg,
  useAddCurriServiceLeg,
  useAddSabreCommercialLegs,
  useAddSupplementalLiftLeg,
  useAddTmdxAffiliateLeg,
  useAddTmdxAviaLeg,
  useAddTmdxDriveAffiliateLeg,
  useAddTmdxDriveTedsLeg,
  useAddTxCenterAviaLeg,
  useAddTxCenterGroundLeg,
  useAddSelfServicedGroundLeg,
  useRemoveRouteHubs,
  useRemoveLeg,
  useRemoveSabreCommercialLegs,
  useSetRouteHubs,
  useUpdateAccommodationLeg,
  useUpdateCommercialLeg,
  useUpdateCurriServiceLeg,
  useUpdateSabreCommercialLeg,
  useUpdateSupplementalLiftLeg,
  useUpdateTmdxAffiliateLeg,
  useUpdateTmdxAviaLeg,
  useUpdateTmdxDriveAffiliateLeg,
  useUpdateTmdxDriveTedsLeg,
  useUpdateTxCenterAviaLeg,
  useUpdateTxCenterGroundLeg,
  useUpdateSelfServicedGroundLeg,
} from '@/queries/route-planner';
import {
  AddCommercialAviaLeg,
  AddSabreCommercialAviaLegs,
  AviaLegTypes,
  CommercialFlightAviaLeg,
  CommercialLegType,
  GroundLegTypes,
  HubTypeEnum,
  RouteLegType,
  RouteLegTypeEnum,
  RouteTypeEnum,
  SegmentTypeEnum,
  TmdxJetAviaLeg,
} from '@/api/apis/route';
import { retrieveUnifiedError } from '@/api/utils';
import { CaseFacility, CaseResourceType } from '@/api/apis/case';
import { Hub } from '@/api/apis/facilities';
import { LOGISTICS_VERSION_MISMATCH_ERROR_CODE } from '@/queries/route-planner/utils';
import { RouteFilterType, RouteSegmentType } from '@/types/route';
import { queryClient } from '@/queries';
import { driveServiceQuery } from '@/queries/drive-service';
import { LogisticsGroundTrip } from '@/api/apis/drive-service';
import { useCaseDetails } from '@/hooks/useCaseDetails';

export type UseRouteType = {
  handleOpenNewLegModal: (data: Partial<HandleOpenNewLegModal>) => void;
  handleLegSubmit: (data: LegFormValues) => Promise<void>;
  handleHubSelect: (selectedHub: Hub, segmentType: SegmentTypeEnum) => void;
  handleHubModalOpen: (segmentType: SegmentTypeEnum) => void;
  handleRemoveHub: (segmentType: SegmentTypeEnum) => void;
  handleEditLegModalOpen: (leg: RouteLegType, segmentId: string | null) => void;
};

type UseRouteProps = {
  routeType: RouteTypeEnum;
  filterType: RouteFilterType;
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
};

export const useRoute = ({
  routeType,
  filterType,
  donorHospital,
  txCenter,
  segments,
  legs,
}: UseRouteProps): UseRouteType => {
  const { id: logisticsId, caseId } = useLogisticsData();
  const routeData = useRouteDetails(routeType);
  const { list } = useRouteLocations();

  const getSegmentLocations = useSegmentLocations({
    segments,
    donorHospital,
    txCenter,
    legs,
  });

  const { resources } = useCaseDetails();
  const reservedAircrafts = useMemo(() => resources.filter((r) => r.resourceType === 'Aircraft'), [resources]);
  // Hubs
  const { mutateAsync: setRouteHubs } = useSetRouteHubs();
  const { mutateAsync: removeRouteHubs } = useRemoveRouteHubs();
  // Add legs
  const { mutateAsync: addTmdxAviaLeg } = useAddTmdxAviaLeg();
  const { mutateAsync: addSupplementalLiftLeg } = useAddSupplementalLiftLeg();
  const { mutateAsync: addTxCenterAviaLeg } = useAddTxCenterAviaLeg();
  const { mutateAsync: addCommercialLeg } = useAddCommercialLeg();
  const { mutateAsync: addSabreCommercialLegs } = useAddSabreCommercialLegs();
  const { mutateAsync: addTmdxAffiliateLeg } = useAddTmdxAffiliateLeg();
  const { mutateAsync: addTmdxDriveAffiliateLeg } = useAddTmdxDriveAffiliateLeg();
  const { mutateAsync: addTmdxDriveTedsLeg } = useAddTmdxDriveTedsLeg();
  const { mutateAsync: addTxCenterGroundLeg } = useAddTxCenterGroundLeg();
  const { mutateAsync: addCurriServiceLeg } = useAddCurriServiceLeg();
  const { mutateAsync: addSelfServicedGroundLeg } = useAddSelfServicedGroundLeg();
  const { mutateAsync: addAccommodationLeg } = useAddAccommodationLeg();
  // Update legs
  const { mutateAsync: updateTmdxAviaLeg } = useUpdateTmdxAviaLeg();
  const { mutateAsync: updateSupplementalLiftLeg } = useUpdateSupplementalLiftLeg();
  const { mutateAsync: updateTxCenterAviaLeg } = useUpdateTxCenterAviaLeg();
  const { mutateAsync: updateCommercialLeg } = useUpdateCommercialLeg();
  const { mutateAsync: updateSabreCommercialLeg } = useUpdateSabreCommercialLeg();
  const { mutateAsync: updateTmdxAffiliateLeg } = useUpdateTmdxAffiliateLeg();
  const { mutateAsync: updateTmdxDriveAffiliateLeg } = useUpdateTmdxDriveAffiliateLeg();
  const { mutateAsync: updateTmdxDriveTedsLeg } = useUpdateTmdxDriveTedsLeg();
  const { mutateAsync: updateTxCenterGroundLeg } = useUpdateTxCenterGroundLeg();
  const { mutateAsync: updateCurriServiceLeg } = useUpdateCurriServiceLeg();
  const { mutateAsync: updateSelfServicedGroundLeg } = useUpdateSelfServicedGroundLeg();
  const { mutateAsync: updateAccommodationLeg } = useUpdateAccommodationLeg();
  // Remove leg
  const { mutateAsync: removeSabreCommercialLegs } = useRemoveSabreCommercialLegs();
  const { mutateAsync: removeLeg } = useRemoveLeg();

  const { onOpenModal, setIsLoading, onCloseModal } = useRouteModalActions();
  const { handleResourceAssignment } = useResourcesHandlers({ id: caseId });

  const handleAircraftReservation = useCallback(
    async ({ type, data, legId }: HandleAircraftReservationType) => {
      switch (type) {
        case 'edit':
          const changedAircraft = data.resources?.find((r) => r.resourceType === CaseResourceType.Aircraft);

          if (changedAircraft) {
            const originalLeg = routeData.legs.find((l) => l.id === legId);
            const originalAircraft = originalLeg?.resources.find((r) => r.resourceType === CaseResourceType.Aircraft);
            const originalAircraftResource = reservedAircrafts.find(
              (a) => a.resourceId === originalAircraft?.resourceId,
            );

            if (originalAircraft?.resourceId === changedAircraft.resourceId && originalAircraftResource) {
              break;
            }

            const legList = routeData.legs.filter((l) => l.id !== legId);
            const isOriginalAircraftInUse = legList.some((leg) =>
              leg.resources.some((r) => r.resourceId === originalAircraft?.resourceId),
            );

            if (originalAircraftResource && !isOriginalAircraftInUse) {
              await handleResourceAssignment({ data: { aircraftIds: [originalAircraft!.resourceId] }, type: 'remove' });
            }
          }
          break;
        case 'remove':
          const leg = routeData.legs.find((l) => l.id === legId);
          const filteredLegList = routeData.legs.filter((l) => l.id !== legId);
          const usedAircraft = leg?.resources.find((r) => r.resourceType === CaseResourceType.Aircraft);

          if (usedAircraft) {
            const isAircraftInUse = filteredLegList.some((l) =>
              l.resources.some((r) => r.resourceId === usedAircraft?.resourceId),
            );
            const usedAircraftResource = reservedAircrafts.find((a) => a.resourceId === usedAircraft?.resourceId);

            if (usedAircraftResource && !isAircraftInUse) {
              await handleResourceAssignment({ data: { aircraftIds: [usedAircraft.resourceId] }, type: 'remove' });
            }
          }
          break;
        default:
          break;
      }
    },
    [reservedAircrafts, routeData.legs, handleResourceAssignment],
  );

  const handleLegSubmit = useCallback(
    async (data: LegFormValues) => {
      try {
        setIsLoading(true);
        const { version } = getLogisticsDataById(logisticsId);

        switch (data.legType) {
          case RouteLegTypeEnum.TmdxJetAviaLeg:
            await addTmdxAviaLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.SupplementalLiftAviaLeg:
            await addSupplementalLiftLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.TxCenterProviderAviaLeg:
            await addTxCenterAviaLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.CommercialFlightAviaLeg:
            const inputData = convertRouteLegFormValuesToRouteLeg({ type: data.legType, data });
            if (data.connectedLegs.length) {
              await addSabreCommercialLegs({
                logisticsId,
                routeId: routeData.id,
                version,
                data: inputData as AddSabreCommercialAviaLegs,
              });
            } else {
              await addCommercialLeg({
                logisticsId,
                routeId: routeData.id,
                version,
                data: inputData as AddCommercialAviaLeg,
              });
            }
            break;
          case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
            await addTmdxAffiliateLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
            await addTmdxDriveAffiliateLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.TxCenterProviderGroundLeg:
            await addTxCenterGroundLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.CurriServiceGroundLeg:
            await addCurriServiceLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
            await addTmdxDriveTedsLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.SelfServicedGroundLeg:
            await addSelfServicedGroundLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          case RouteLegTypeEnum.AccommodationLeg:
            await addAccommodationLeg({
              logisticsId,
              routeId: routeData.id,
              version,
              data: convertRouteLegFormValuesToRouteLeg({ type: data.legType, data }),
            });
            break;
          default:
            break;
        }

        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);

        if (error.name === LOGISTICS_VERSION_MISMATCH_ERROR_CODE) {
          onCloseModal();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      onCloseModal,
      addTmdxAviaLeg,
      logisticsId,
      routeData.id,
      addSupplementalLiftLeg,
      addTxCenterAviaLeg,
      addTmdxAffiliateLeg,
      addTmdxDriveAffiliateLeg,
      addTxCenterGroundLeg,
      addCurriServiceLeg,
      addTmdxDriveTedsLeg,
      addSelfServicedGroundLeg,
      addAccommodationLeg,
      addSabreCommercialLegs,
      addCommercialLeg,
    ],
  );

  const handleLegEdit = useCallback(
    async (data: LegFormValues) => {
      try {
        setIsLoading(true);
        const { version } = getLogisticsDataById(logisticsId);

        const currentLegList =
          data.legType === RouteLegTypeEnum.AccommodationLeg ? routeData.accommodationLegs : routeData.legs;

        const currentLeg = currentLegList.find((l) => l.id === data.id)!;

        const saveObject = {
          logisticsId,
          routeId: routeData.id,
          version,
          leg: currentLeg,
          formData: data,
        };

        switch (data.legType) {
          case RouteLegTypeEnum.TmdxJetAviaLeg:
            const tmdxJetUpdate = getUpdatedRouteLegValues({
              type: data.legType,
              data,
              leg: currentLeg as TmdxJetAviaLeg,
            });

            if (Object.keys(tmdxJetUpdate).length) {
              await handleAircraftReservation({ type: 'edit', data: tmdxJetUpdate, legId: data.id });
              await updateTmdxAviaLeg({
                logisticsId,
                routeId: routeData.id,
                legId: data.id,
                version,
                data: tmdxJetUpdate,
              });
            }

            break;
          case RouteLegTypeEnum.SupplementalLiftAviaLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateSupplementalLiftLeg,
            });

            break;
          case RouteLegTypeEnum.TxCenterProviderAviaLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateTxCenterAviaLeg,
            });

            break;
          case RouteLegTypeEnum.CommercialFlightAviaLeg:
            if ((currentLeg as CommercialFlightAviaLeg).commercialLegType === CommercialLegType.Sabre) {
              await saveUpdatedLeg({
                ...saveObject,
                mutation: updateSabreCommercialLeg,
              });
            } else {
              await saveUpdatedLeg({
                ...saveObject,
                mutation: updateCommercialLeg,
              });
            }

            break;
          case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateTmdxAffiliateLeg,
            });

            break;
          case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateTmdxDriveAffiliateLeg,
            });

            break;
          case RouteLegTypeEnum.TxCenterProviderGroundLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateTxCenterGroundLeg,
            });

            break;
          case RouteLegTypeEnum.CurriServiceGroundLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateCurriServiceLeg,
            });

            break;
          case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateTmdxDriveTedsLeg,
            });

            break;
          case RouteLegTypeEnum.SelfServicedGroundLeg:
            await saveUpdatedLeg({
              ...saveObject,
              mutation: updateSelfServicedGroundLeg,
            });

            break;
          case RouteLegTypeEnum.AccommodationLeg:
            await saveUpdatedLeg({
              ...saveObject,
              locations: list,
              mutation: updateAccommodationLeg,
            });
            break;
          default:
            break;
        }

        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);

        if (error.name === LOGISTICS_VERSION_MISMATCH_ERROR_CODE) {
          onCloseModal();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      routeData.accommodationLegs,
      routeData.legs,
      routeData.id,
      logisticsId,
      onCloseModal,
      updateSupplementalLiftLeg,
      updateTxCenterAviaLeg,
      updateTmdxAffiliateLeg,
      updateTmdxDriveAffiliateLeg,
      updateTxCenterGroundLeg,
      updateCurriServiceLeg,
      updateTmdxDriveTedsLeg,
      updateSelfServicedGroundLeg,
      list,
      updateAccommodationLeg,
      handleAircraftReservation,
      updateTmdxAviaLeg,
      updateSabreCommercialLeg,
      updateCommercialLeg,
    ],
  );

  const handleOpenNewLegModal = useCallback(
    ({ segmentId, prevLegId }: Partial<HandleOpenNewLegModal>) => {
      const currentSegment = segmentId ? getSegment(segments, segmentId) : null;
      const prevLeg = currentSegment && prevLegId ? getPreviousLeg(legs, currentSegment, prevLegId) : null;

      onOpenModal({
        type: RouteModalTypes.AddNewLeg,
        context: {
          routeType,
          currentSegment,
          prevLeg,
          getSegmentLocations,
          onSubmit: handleLegSubmit,
        },
      });
    },
    [routeType, handleLegSubmit, legs, onOpenModal, segments, getSegmentLocations],
  );

  const handleHubSelect = useCallback(
    async (selectedHub: Hub, segmentType: SegmentTypeEnum) => {
      try {
        setIsLoading(true);
        const { version } = getLogisticsDataById(logisticsId);
        const assignHubType =
          segmentType === SegmentTypeEnum.hospital ? HubTypeEnum.DepartureHub : HubTypeEnum.ArrivalHub;

        const isAssignBothHubs =
          routeData.routeType === RouteTypeEnum.Main && !routeData.arrivalHub && !routeData.departureHub;

        await setRouteHubs({
          logisticsId,
          hubs: [
            {
              routeId: routeData.id,
              version,
              type: assignHubType,
              hubId: selectedHub.id,
            },
            ...(isAssignBothHubs
              ? [
                  {
                    routeId: routeData.id,
                    version,
                    type:
                      assignHubType === HubTypeEnum.DepartureHub ? HubTypeEnum.ArrivalHub : HubTypeEnum.DepartureHub,
                    hubId: selectedHub.id,
                  },
                ]
              : []),
          ],
        });

        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);

        if (error.name === LOGISTICS_VERSION_MISMATCH_ERROR_CODE) {
          onCloseModal();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      setRouteHubs,
      routeData.arrivalHub,
      routeData.departureHub,
      routeData.id,
      routeData.routeType,
      onCloseModal,
      logisticsId,
    ],
  );

  const handleHubModalOpen = useCallback(
    (segmentType: SegmentTypeEnum) => {
      onOpenModal({
        type: RouteModalTypes.AssignHub,
        context: {
          donorHospital,
          segmentType,
          onSelect: handleHubSelect,
        },
      });
    },
    [onOpenModal, donorHospital, handleHubSelect],
  );

  const handleRemoveHub = useCallback(
    async (segmentType: SegmentTypeEnum) => {
      try {
        setIsLoading(true);
        const { version } = getLogisticsDataById(logisticsId);
        const removeHubType =
          segmentType === SegmentTypeEnum.hospital ? HubTypeEnum.DepartureHub : HubTypeEnum.ArrivalHub;

        await removeRouteHubs({
          logisticsId,
          hubs: [
            {
              routeId: routeData.id,
              version,
              type: removeHubType,
            },
          ],
        });

        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);

        if (error.name === LOGISTICS_VERSION_MISMATCH_ERROR_CODE) {
          onCloseModal();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, removeRouteHubs, logisticsId, routeData.id, onCloseModal],
  );

  const handleConnectedCommercialLegsDelete = useCallback(
    async (sabreGroupId: string) => {
      try {
        setIsLoading(true);
        const { version } = getLogisticsDataById(logisticsId);
        await removeSabreCommercialLegs({ logisticsId, routeId: routeData.id, sabreGroupId, version });
        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);

        if (error.name === LOGISTICS_VERSION_MISMATCH_ERROR_CODE) {
          onCloseModal();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [logisticsId, onCloseModal, removeSabreCommercialLegs, routeData.id, setIsLoading],
  );

  const sendLegDeleteRequest = useCallback(
    async (legId: string) => {
      try {
        setIsLoading(true);
        const { version } = getLogisticsDataById(logisticsId);
        await removeLeg({ logisticsId, routeId: routeData.id, legId, version });
        onCloseModal();
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);

        if (error.name === LOGISTICS_VERSION_MISMATCH_ERROR_CODE) {
          onCloseModal();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [logisticsId, onCloseModal, removeLeg, routeData.id, setIsLoading],
  );

  const handleCommercialLegRemove = useCallback(
    async (leg: BaseCommercialAviaLegSchemaValues) => {
      const currentLeg = routeData.legs.find((l): l is CommercialFlightAviaLeg => l.id === leg.id);

      if (!currentLeg) {
        onCloseModal();
        toast.error('Current route leg not found');
        return;
      }
      const { version } = getLogisticsDataById(logisticsId);
      const isSabreLeg = currentLeg.commercialLegType === CommercialLegType.Sabre && !!currentLeg.sabreGroupId;

      if (!isSabreLeg) {
        await removeLeg({ logisticsId, routeId: routeData.id, legId: leg.id, version });
        onCloseModal();
        return;
      }

      const sabreLegs = routeData.legs.filter(
        (l): l is CommercialFlightAviaLeg =>
          l.legType === RouteLegTypeEnum.CommercialFlightAviaLeg && l.sabreGroupId === currentLeg.sabreGroupId,
      );

      if (sabreLegs.length > 1) {
        onOpenModal({
          type: RouteModalTypes.RemoveConnectedLegs,
          context: {
            legs: sabreLegs,
            onRemove: handleConnectedCommercialLegsDelete,
          },
        });
      } else if (sabreLegs.length === 1) {
        await removeSabreCommercialLegs({
          logisticsId,
          routeId: routeData.id,
          sabreGroupId: sabreLegs[0].sabreGroupId!,
          version,
        });

        onCloseModal();
      }
    },
    [
      handleConnectedCommercialLegsDelete,
      logisticsId,
      onCloseModal,
      onOpenModal,
      removeLeg,
      removeSabreCommercialLegs,
      routeData.id,
      routeData.legs,
    ],
  );

  const handleGroundTripLegRemove = useCallback(
    async (leg: BaseTmdxDriveAffiliateGroundSchemaValues | BaseTmdxDriveTedsGroundSchemaValues) => {
      const currentGroundTrip = leg.tripId
        ? queryClient.getQueryData<LogisticsGroundTrip>(driveServiceQuery.item(leg.tripId).queryKey)
        : null;

      const isActiveTrip = currentGroundTrip && ACTIVE_GROUND_TRIP_STATUSES.includes(currentGroundTrip.status);

      if (!leg.tripId || !currentGroundTrip || !isActiveTrip) {
        await sendLegDeleteRequest(leg.id);
        return;
      }

      onOpenModal({
        type: RouteModalTypes.RemoveActiveGroundTripLeg,
        context: {
          legId: leg.id,
          tripNumber: currentGroundTrip.tripNumber,
          tripStatus: currentGroundTrip.status,
          onRemove: sendLegDeleteRequest,
        },
      });
    },
    [onOpenModal, sendLegDeleteRequest],
  );

  const handleRemoveLeg = useCallback(
    async (leg: LegFormValues) => {
      switch (leg.legType) {
        case RouteLegTypeEnum.TmdxJetAviaLeg:
          await handleAircraftReservation({ type: 'remove', legId: leg.id });
          await sendLegDeleteRequest(leg.id);
          break;
        case RouteLegTypeEnum.CommercialFlightAviaLeg:
          await handleCommercialLegRemove(leg);
          break;
        case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
        case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
          await handleGroundTripLegRemove(leg);
          break;
        default:
          await sendLegDeleteRequest(leg.id);
      }
    },
    [handleAircraftReservation, sendLegDeleteRequest, handleCommercialLegRemove, handleGroundTripLegRemove],
  );

  const handleEditLegModalOpen = useCallback(
    (leg: RouteLegType, segmentId: string | null) => {
      const currentSegment = segmentId ? getSegment(segments, segmentId) : null;

      onOpenModal({
        type: RouteModalTypes.EditLeg,
        context: {
          routeType,
          filterType,
          currentLeg: leg,
          currentSegment,
          getSegmentLocations,
          onSubmit: handleLegEdit,
          onDelete: handleRemoveLeg,
        },
      });
    },
    [segments, onOpenModal, routeType, filterType, getSegmentLocations, handleLegEdit, handleRemoveLeg],
  );

  return {
    handleLegSubmit,
    handleOpenNewLegModal,
    handleHubSelect,
    handleHubModalOpen,
    handleRemoveHub,
    handleEditLegModalOpen,
  };
};
